/**
 * 운전 화면 전체 (계획서 §13.1, UI_design.pptx).
 *
 * ```text
 * ┌ 좌 제어 ──┬ 중앙 공정도 ──┬ 우 A/B 비교 ┐
 * │ 고장/시계 │               │             │
 * ├───────────┤               │             │
 * │ 탭 영역   │               │             │
 * │ 운전현황  │               │             │
 * │ Agent…    │               │             │
 * └───────────┴───────────────┴─────────────┘
 * ```
 *
 * 탭은 원래 화면 맨 아래 전체 너비였는데, 공정도와 A/B 패널이 길어 화면 밖으로 밀려
 * 보이지 않았다. 2026-09-04 에 좌측 제어 패널 아래로 옮겼다.
 *
 * 상태는 WebSocket 으로 밀려오고, sequence 가 끊기면 REST snapshot 으로 복구한다 (§12.6).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ApiError,
  confirmRisk,
  controlApprovalTimer,
  controlClock,
  decidePlan,
  fetchAgents,
  fetchCurrentRun,
  humanStart,
  raiseIncident,
  requestEarlyCompletion,
  resetRun,
} from "@/api/client";
import type { RunSnapshot, StrategyCode } from "@/types/api";
import { ActionBanner } from "@/features/plan/ActionBanner";
import { ControlPanel } from "@/features/control/ControlPanel";
import { HumanStartModal } from "@/features/plan/HumanStartModal";
import { ProcessDiagram } from "@/features/process/ProcessDiagram";
import { StrategyCompare } from "@/features/plan/StrategyCompare";
import { BottomTabs } from "@/features/tabs/Tabs";
import { InsightPanels } from "@/features/tabs/InsightPanels";

import { useRunConnection } from "./useRunConnection";
import { useSnapshotPolling } from "./useSnapshotPolling";
import { useRunStore } from "./runStore";

/** Agent 는 설비 하나를 담당한다. 공정도에 설명을 얹을 때 쓴다 (§13.1). */
const AGENT_EQUIPMENT: Record<string, string | undefined> = {
  WELDER_AGENT: "WELDER",
  EPOXY_AGENT: "EPOXY",
  DRYER_AGENT: "DRYER",
  DUST_COLLECTOR_AGENT: "DUST_COLLECTOR",
  COMPRESSOR_AGENT: "COMPRESSOR",
};

export function OperationScreen(): React.JSX.Element {
  const snapshot = useRunStore((s) => s.snapshot);
  const connection = useRunStore((s) => s.connection);
  const setSnapshot = useRunStore((s) => s.setSnapshot);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<string | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [messageId, setMessageId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const lastEventAt = useRunStore((s) => s.lastEventAt);

  const snapshotSync = useRunConnection(snapshot?.run_id ?? null);

  const runId = snapshot?.run_id ?? null;
  // 시계가 도는 동안 설비 수치를 2초마다 다시 읽는다. `clock.updated` 는 시계만
  // 갱신하므로 이것이 없으면 새로고침해야 수치가 바뀐다 (§13.2).
  useSnapshotPolling(runId, snapshot?.clock.is_playing ?? false);
  const { data: agentData } = useQuery({
    queryKey: ["agents", runId],
    queryFn: () => fetchAgents(runId ?? ""),
    enabled: runId !== null,
    refetchInterval: 5_000,
  });

  // WebSocket event 가 오면 곧바로 다시 읽는다. 5초 polling 만으로는 고장 직후
  // "아직 없습니다" 가 잠시 남아 사람이 멈춘 줄 안다.
  useEffect(() => {
    if (lastEventAt === null) return;
    void queryClient.invalidateQueries({ queryKey: ["agents"] });
    void queryClient.invalidateQueries({ queryKey: ["workflow"] });
    void queryClient.invalidateQueries({ queryKey: ["ontology"] });
  }, [lastEventAt, queryClient]);

  // Agent 설명을 공정도와 A/B 패널에 나눠 준다. 숫자를 만드는 주체가 아니다 (§8.2).
  const aiSummaries = useMemo(() => {
    const byEquipment: Record<string, string | undefined> = {};
    for (const agent of agentData?.agents ?? []) {
      const equipment = AGENT_EQUIPMENT[agent.agent_id];
      if (equipment && agent.advice)
        byEquipment[equipment] = agent.advice.display_message;
    }
    return byEquipment;
  }, [agentData]);

  const statusCenter = (agentData?.agents ?? []).find(
    (agent) => agent.agent_id === "STATUS_CENTER",
  );

  // 최초 진입 시 현재 run 을 읽는다. 없으면 backend 가 만들어 준다.
  useEffect(() => {
    let cancelled = false;
    void fetchCurrentRun()
      .then((initial) => {
        if (!cancelled) setSnapshot(initial);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(describe(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [setSnapshot]);

  const act = useCallback(
    async (operation: () => Promise<RunSnapshot>) => {
      setBusy(true);
      setError(null);
      try {
        setSnapshot(await operation());
      } catch (cause) {
        setError(describe(cause));
      } finally {
        setBusy(false);
      }
    },
    [setSnapshot],
  );

  if (!snapshot) {
    return (
      <main className="operation-loading">
        <p>{error ?? "운전 상태를 불러오는 중…"}</p>
      </main>
    );
  }

  const version = snapshot.plan.plan_version;
  const selected = snapshot.selected_strategy;

  return (
    <main className="operation">
      <ActionBanner
        snapshot={snapshot}
        busy={busy}
        onApprove={(strategy) => {
          if (version === undefined) return;
          void act(() => decidePlan(version, "approve", strategy));
        }}
        onHumanStart={() => {
          setMessageId(null);
          setModalOpen(true);
        }}
        onConfirmRisk={(confirmed) =>
          void act(() => confirmRisk(snapshot.run_id, confirmed))
        }
      />

      <div className="operation-top">
        <div className="operation-left">
          <ControlPanel
            snapshot={snapshot}
            busy={busy}
            error={error}
            onRaiseIncident={() =>
              void act(() => raiseIncident(snapshot.run_id))
            }
            onEarlyComplete={() =>
              void act(() => requestEarlyCompletion(snapshot.run_id))
            }
            onConfirmRisk={(confirmed) =>
              void act(() => confirmRisk(snapshot.run_id, confirmed))
            }
            onSeek={(value) =>
              void act(() =>
                controlClock(snapshot.run_id, {
                  action: "seek",
                  virtual_at: value,
                }),
              )
            }
            onSpeed={(multiplier) =>
              void act(() =>
                controlClock(snapshot.run_id, {
                  action: "speed",
                  speed_multiplier: multiplier,
                }),
              )
            }
            onPlayPause={() =>
              void act(() =>
                controlClock(snapshot.run_id, {
                  action: snapshot.clock.is_playing ? "pause" : "play",
                }),
              )
            }
            onReset={() => void act(resetRun)}
          />

          <BottomTabs snapshot={snapshot} />
        </div>

        <section className="diagram-panel" aria-label="공정도">
          <ProcessDiagram
            equipment={snapshot.equipment}
            aiSummaries={aiSummaries}
            selectedId={selectedEquipment}
            onSelect={setSelectedEquipment}
          />
          <p className={`connection connection-${connection}`} role="status">
            실시간 연결: {connectionLabel(connection)}
          </p>
          {snapshotSync === "retrying" && (
            <p className="notice" role="status">운전 상태를 다시 불러오는 중입니다. 자동으로 재시도합니다.</p>
          )}
          {snapshotSync === "failed" && (
            <p className="error" role="alert">운전 상태를 불러오지 못했습니다. 새로고침 후 로그인 상태를 확인해 주세요.</p>
          )}
          <InsightPanels runId={snapshot.run_id} />
        </section>

        <StrategyCompare
          snapshot={snapshot}
          aiSummary={statusCenter?.advice?.display_message ?? null}
          aiPending={agentData?.analyzing ?? false}
          busy={busy}
          onDecide={(decision, strategy) => {
            if (version === undefined) return;
            void act(() => decidePlan(version, decision, strategy));
          }}
          onTimer={(action) => {
            if (version === undefined) return;
            void act(() => controlApprovalTimer(version, action));
          }}
          onHumanStart={() => {
            setMessageId(null);
            setModalOpen(true);
          }}
        />
      </div>

      <HumanStartModal
        open={modalOpen}
        strategy={selected}
        candidate={
          selected && snapshot.plan.candidates
            ? snapshot.plan.candidates[selected]
            : null
        }
        busy={busy}
        messageId={messageId}
        onConfirm={() => {
          if (version === undefined) return;
          setBusy(true);
          void humanStart(version)
            .then((response) => {
              setMessageId(response.message_id);
            })
            .catch((cause: unknown) => {
              setError(describe(cause));
              setModalOpen(false);
            })
            .finally(() => {
              setBusy(false);
            });
        }}
        onClose={() => {
          setModalOpen(false);
        }}
      />
    </main>
  );
}

function connectionLabel(state: string): string {
  if (state === "open") return "● 연결됨";
  if (state === "connecting") return "◌ 연결 중";
  if (state === "closed") return "✕ 끊김 (재연결 시도 중)";
  return "○ 대기";
}

function describe(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isStalePlanVersion) {
      return "계획이 이미 갱신되었습니다. 화면을 새로 읽은 뒤 다시 시도하세요.";
    }
    return cause.problem?.detail ?? cause.message;
  }
  return "요청을 처리하지 못했습니다.";
}

export type { StrategyCode };
